import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { date } from '../lib/format';

export default function Agreements() {
  const { activeId, activeBusiness } = useBusiness();
  const { can } = useAuth();
  const editable = can('contracts.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null); // full detail (with .template.fields)
  const [newReq, setNewReq] = useState(null);    // {template_key, customer_id} while creating
  const [form, setForm] = useState(null);        // {title, field_values}
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editingDoc, setEditingDoc] = useState(false);
  const [doc, setDoc] = useState([]);

  const toEditable = (blocks) => (blocks || []).map((b) => (
    b.sign ? { type: 'sign' } : b.h != null ? { type: 'h', text: b.h } : b.li ? { type: 'li', text: (b.li || []).join('\n') } : { type: 'p', text: b.p || '' }
  ));
  const fromEditable = (xs) => xs.map((it) => (
    it.type === 'sign' ? { sign: true } : it.type === 'h' ? { h: it.text || '' } : it.type === 'li' ? { li: (it.text || '').split('\n').map((s) => s.trim()).filter(Boolean) } : { p: it.text || '' }
  ));
  const setBlk = (i, v) => setDoc((d) => d.map((b, idx) => (idx === i ? { ...b, ...v } : b)));
  const moveBlk = (i, dir) => setDoc((d) => { const j = i + dir; if (j < 0 || j >= d.length) return d; const n = [...d]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const delBlk = (i) => setDoc((d) => d.filter((_, idx) => idx !== i));
  const addBlk = (type) => setDoc((d) => [...d, type === 'sign' ? { type: 'sign' } : type === 'h' ? { type: 'h', text: '' } : type === 'li' ? { type: 'li', text: '' } : { type: 'p', text: '' }]);

  function startEditDoc() { setDoc(toEditable(current.source_blocks)); setEditingDoc(true); setError(null); }
  async function saveDoc() {
    setSaving(true); setError(null);
    try {
      await api(`/agreements/${current.id}`, { method: 'PUT', body: { body_blocks: fromEditable(doc) } });
      const full = await api(`/agreements/${current.id}`); setCurrent(full); setEditingDoc(false); loadList();
    } catch (e) { setError(e?.detail || 'Could not save the document.'); }
    finally { setSaving(false); }
  }
  async function revertDoc() {
    setBusy(true); setError(null);
    try {
      await api(`/agreements/${current.id}`, { method: 'PUT', body: { body_blocks: null } });
      const full = await api(`/agreements/${current.id}`); setCurrent(full); setEditingDoc(false);
    } catch (e) { setError(e?.detail || 'Could not revert.'); }
    finally { setBusy(false); }
  }

  const loadList = useCallback(() => {
    setLoading(true);
    api(`/agreements?business_id=${activeId}`).then((d) => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [activeId]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    api('/agreements/templates').then((d) => setTemplates(Array.isArray(d) ? d : [])).catch(() => setTemplates([]));
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
  }, [activeId]);

  function openNew() {
    setCurrent(null); setForm(null); setError(null);
    setNewReq({ template_key: templates[0]?.key || '', customer_id: '' });
    setOpen(true);
  }
  async function openRow(row) {
    setNewReq(null); setError(null); setOpen(true);
    try {
      const a = await api(`/agreements/${row.id}`);
      setCurrent(a);
      setForm({ title: a.title, field_values: a.field_values || {} });
    } catch { setOpen(false); }
  }

  async function createDraft() {
    setBusy(true); setError(null);
    try {
      const a = await api('/agreements', { method: 'POST', body: { business_id: activeId, customer_id: newReq.customer_id, template_key: newReq.template_key } });
      const full = await api(`/agreements/${a.id}`);
      setCurrent(full); setForm({ title: full.title, field_values: full.field_values || {} });
      setNewReq(null); loadList();
    } catch (e) { setError(e?.detail || 'Could not create.'); }
    finally { setBusy(false); }
  }

  const setFV = (k, v) => setForm((f) => ({ ...f, field_values: { ...f.field_values, [k]: v } }));

  async function save() {
    setSaving(true); setError(null);
    try {
      const a = await api(`/agreements/${current.id}`, { method: 'PUT', body: { title: form.title, field_values: form.field_values } });
      const full = await api(`/agreements/${a.id}`); setCurrent(full); loadList();
    } catch (e) { setError(e?.detail || 'Could not save.'); }
    finally { setSaving(false); }
  }

  async function action(path, label) {
    setBusy(true); setError(null);
    try {
      await api(`/agreements/${current.id}/${path}`, { method: 'POST' });
      const full = await api(`/agreements/${current.id}`); setCurrent(full); loadList();
    } catch (e) { setError(e?.detail || `Could not ${label}.`); }
    finally { setBusy(false); }
  }

  async function removeAgreement() {
    if (!window.confirm('Delete this agreement? This cannot be undone.')) return;
    setBusy(true); setError(null);
    try {
      await api(`/agreements/${current.id}`, { method: 'DELETE' });
      setOpen(false); loadList();
    } catch (e) { setError(e?.detail || 'Could not delete.'); }
    finally { setBusy(false); }
  }

  const preview = () => window.open(`/api/agreements/${current.id}/pdf`, '_blank', 'noopener');

  const columns = [
    { key: 'title', header: 'Agreement' },
    { key: 'customer_name', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'created_at', header: 'Created', render: (r) => date(r.created_at) },
    { key: 'signed_at', header: 'Signed', render: (r) => (r.signed_at ? date(r.signed_at) : '—') },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const isDraft = current?.status === 'draft';
  const fields = current?.template?.fields || [];

  const footer = current ? (
    editingDoc ? (
      <>
        <button className="btn" onClick={() => setEditingDoc(false)} disabled={saving}>Cancel</button>
        <button className="btn btn--primary" onClick={saveDoc} disabled={saving}>{saving ? 'Saving…' : 'Save document'}</button>
      </>
    ) : (
      <>
        <button className="btn" onClick={() => !saving && setOpen(false)}>Close</button>
        <button className="btn" onClick={preview}>Preview PDF</button>
        {editable && isDraft && <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
        {editable && ['draft', 'sent'].includes(current.status) && <button className="btn btn--primary" onClick={() => action('send', 'send')} disabled={busy}>{current.status === 'sent' ? 'Re-send' : 'Send to sign'}</button>}
      </>
    )
  ) : (
    <>
      <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
      <button className="btn btn--primary" onClick={createDraft} disabled={busy || !newReq?.customer_id || !newReq?.template_key}>{busy ? 'Creating…' : 'Create draft'}</button>
    </>
  );

  return (
    <>
      <PageHeader title="Agreements" subtitle={activeBusiness?.name}
        actions={editable && <button className="btn btn--primary" onClick={openNew}>New agreement</button>} />

      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} onRowClick={openRow} emptyMessage="No agreements yet." />
      </section>

      <Drawer open={open} title={current ? (current.title || 'Agreement') : 'New agreement'} width={680} onClose={() => !saving && setOpen(false)} footer={footer}>
        {newReq && !current && (
          <>
            <div className="field"><label>Template</label>
              <select value={newReq.template_key} onChange={(e) => setNewReq((r) => ({ ...r, template_key: e.target.value }))}>
                {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Customer</label>
              <select value={newReq.customer_id} onChange={(e) => setNewReq((r) => ({ ...r, customer_id: e.target.value }))}>
                <option value="">Select…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <p className="hint">A draft is created from the template with the customer's details pre-filled. You can edit fields, preview, then send for signature.</p>
            {error && <div className="form-error">{error}</div>}
          </>
        )}

        {current && form && (
          <>
            <div className="appr-status">
              <StatusBadge status={current.status} />
              {current.status === 'sent' && current.sent_at && <span className="hint">Sent {date(current.sent_at)} — awaiting signature</span>}
              {current.status === 'signed' && <span className="hint">Signed by {current.client_signer_name} on {date(current.signed_at)}</span>}
            </div>

            <div className="field"><label>Title</label>
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} disabled={!isDraft || editingDoc} /></div>

            {editingDoc ? (
              <>
                <h3 className="form-section">Edit document</h3>
                <p className="hint" style={{ marginBottom: 10 }}>Add, edit, reorder, or remove clauses. You can use merge fields in any text — they fill in automatically: {fields.map((f) => `{{${f.key}}}`).join(', ')}</p>
                <div className="adoc">
                  {doc.map((b, i) => (
                    <div className="adoc__blk" key={i}>
                      <div className="adoc__bar">
                        <select value={b.type} onChange={(e) => setBlk(i, { type: e.target.value })}>
                          <option value="h">Heading</option>
                          <option value="p">Paragraph</option>
                          <option value="li">Bullet list</option>
                          <option value="sign">Signature block</option>
                        </select>
                        <span className="adoc__spacer" />
                        <button className="btn btn--sm btn--ghost" onClick={() => moveBlk(i, -1)} disabled={i === 0} title="Move up">↑</button>
                        <button className="btn btn--sm btn--ghost" onClick={() => moveBlk(i, 1)} disabled={i === doc.length - 1} title="Move down">↓</button>
                        <button className="btn btn--sm btn--ghost" onClick={() => delBlk(i)} title="Remove">✕</button>
                      </div>
                      {b.type === 'sign' ? <div className="adoc__sign">Signature block (client + contractor signatures)</div>
                        : <textarea rows={b.type === 'li' ? 4 : (b.type === 'h' ? 1 : 3)} value={b.text || ''} placeholder={b.type === 'li' ? 'One bullet per line' : b.type === 'h' ? 'Heading' : 'Paragraph text'} onChange={(e) => setBlk(i, { text: e.target.value })} />}
                    </div>
                  ))}
                </div>
                <div className="adoc__add">
                  <span className="hint">Add:</span>
                  <button className="btn btn--sm" onClick={() => addBlk('h')}>Heading</button>
                  <button className="btn btn--sm" onClick={() => addBlk('p')}>Paragraph</button>
                  <button className="btn btn--sm" onClick={() => addBlk('li')}>Bullets</button>
                  <button className="btn btn--sm" onClick={() => addBlk('sign')}>Signature</button>
                </div>
                {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
              </>
            ) : (
              <>
                <h3 className="form-section">Details</h3>
                <div className="form-grid">
                  {fields.map((f) => (
                    <div className={'field' + (f.type === 'textarea' ? ' field--full' : '')} key={f.key}>
                      <label>{f.label}</label>
                      {f.type === 'textarea'
                        ? <textarea rows={2} value={form.field_values[f.key] || ''} placeholder={f.placeholder || ''} onChange={(e) => setFV(f.key, e.target.value)} disabled={!isDraft} />
                        : <input type={f.type === 'date' ? 'date' : 'text'} value={(form.field_values[f.key] || '').slice ? (f.type === 'date' ? (form.field_values[f.key] || '').slice(0, 10) : form.field_values[f.key]) : form.field_values[f.key] || ''} placeholder={f.placeholder || ''} onChange={(e) => setFV(f.key, e.target.value)} disabled={!isDraft} />}
                    </div>
                  ))}
                </div>

                <h3 className="form-section">Document
                  {current.body_custom && <span className="adoc__badge">Customized</span>}
                </h3>
                {isDraft ? (
                  <div className="quote-actions">
                    <button className="btn btn--sm" onClick={startEditDoc}>{current.body_custom ? 'Edit clauses' : 'Customize document'}</button>
                    {current.body_custom && <button className="btn btn--sm btn--ghost" onClick={revertDoc} disabled={busy}>Revert to template</button>}
                  </div>
                ) : <p className="hint">The document is locked once sent. Use Preview PDF to view it.</p>}

                {current.status === 'signed' && (
                  <div className="quote-actions" style={{ marginTop: 14 }}>
                    <a className="btn btn--sm btn--primary" href={`/api/agreements/${current.id}/pdf?download=1`}>Download signed PDF</a>
                  </div>
                )}
                {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
                {editable && current.status !== 'signed' && (
                  <div className="quote-actions" style={{ marginTop: 14 }}>
                    {['draft', 'sent'].includes(current.status) && <button className="btn btn--sm btn--danger" onClick={() => action('void', 'void')} disabled={busy}>Void</button>}
                    <button className="btn btn--sm btn--danger" onClick={removeAgreement} disabled={busy}>Delete</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
